/**
 * AI画像生成 共通型定義
 */

// ============================================================
// 生成ステータス
// ============================================================

export enum GenerationStatus {
  IDLE = "idle",
  GENERATING = "generating",
  COMPLETE = "complete",
  ERROR = "error",
}

// ============================================================
// AI生成オプション
// ============================================================

export enum AICharacter {
  CAT = "cat",
  DOG = "dog",
  RABBIT = "rabbit",
  BEAR = "bear",
  BIRD = "bird",
  NINJA = "ninja",
  WIZARD = "wizard",
  ROBOT = "robot",
}

export enum AIStyle {
  CYBERPUNK = "cyberpunk",
  ANIME = "anime",
  DRAMATIC = "dramatic",
  COLORING = "coloring",
  PHOTO = "photo",
  RETRO_MANGA = "retro_manga",
  EIGHTIES = "eighties",
  ART_NOUVEAU = "art_nouveau",
  SYNTHWAVE = "synthwave",
}

export enum AIExpression {
  SMILE = "smile",
  COOL = "cool",
  SLEEPY = "sleepy",
  SURPRISED = "surprised",
}

export enum AIAccessory {
  HAT = "hat",
  GLASSES = "glasses",
  RIBBON = "ribbon",
  CROWN = "crown",
}

export enum AIBackground {
  STARRY = "starry",
  MEADOW = "meadow",
  SPACE = "space",
  FLOWER = "flower",
}

export interface AIGenerateOptions {
  gender?: "male" | "female" | "none" | "prefer_not_to_say";
  age?: number;
  // 子ども用フィールド
  favorite_colors?: string[];
  self_introduction?: string;
  best_subjects?: string[];
  // 容姿フィールド
  glasses?: string;
  accessory?: string[];
  hairstyle?: string;
  body_type?: string;
  face_shape?: string;
  // 保護者用フィールド
  occupation?: string;
  job_title?: string;
  organization?: string;
  industry?: string;
  annual_income?: string;
  region?: string;
  city?: string;
  user_role?: string;
  household_members?: string[];
  boys_count?: number;
  girls_count?: number;
  // 追加プロンプト（再生成時のユーザー入力）
  additional_prompt?: string;
}

// 素材画像生成用（自由テキスト入力）
export interface AIGenerateMaterialOptions {
  prompt: string; // ユーザー入力（10文字以内）
  imageCount?: number; // 生成枚数（プランで自動決定、デフォルト1）
}

// ============================================================
// 設定インターフェース
// ============================================================

export interface AICharacterConfig {
  key: AICharacter;
  labelKey: string;
  icon: string;
}

export interface AIStyleConfig {
  key: AIStyle;
  labelKey: string;
  icon: string;
}

export interface AIOptionConfig {
  key: AIExpression | AIAccessory | AIBackground;
  labelKey: string;
}

// ============================================================
// 結果・状態インターフェース
// ============================================================

export interface AIImageResult {
  id: number;
  url: string;
  fileSize: number;
}

export interface GeneratingImageState {
  isGenerating: boolean;
  progress: number;
  partialImage: string | null;
  status: GenerationStatus;
}

// ============================================================
// API型定義
// ============================================================

export interface AIQuotaResponse {
  subscription: {
    type: string;
    displayName: string;
  };
  quota: {
    limit: number;
    used: number;
    remaining: number;
    resetsAt: string;
    unlimited: boolean;
    accessDenied: boolean;
  };
  today: {
    generationCount: number;
    editCount: number;
    totalCount: number;
  };
}

export interface AIImageUsageInfo {
  used: number;
  remaining: number;
  limit: number;
  unlimited: boolean;
}

/** APIから返される完全なAsset型（types.ts のAssetと同等） */
export interface AIGeneratedAsset {
  id: number;
  uid: string;
  type: string;
  name: string;
  tags: string;
  content_type: string;
  file_size: number;
  metadata: { width: string; height: string };
  url: string;
  created_at: string;
}

export interface ImageGenerationSSECallbacks {
  onStart: (requestId: string) => void;
  onPartialImage: (b64Json: string, progress: number, index: number, imageIndex?: number) => void;
  /** 各画像の生成完了時（複数枚の場合は複数回呼ばれる） */
  onComplete: (imageUrl: string, id: number, fileSize: number, imageIndex?: number) => void;
  /** 全画像の生成完了時（usage情報を含む、完全なAssetオブジェクトを返す） */
  onAllComplete?: (assets: AIGeneratedAsset[], usage?: AIImageUsageInfo) => void;
  onError: (code: string, message: string) => void;
}

export type ImagePurpose = "child_profile_icon" | "parent_profile_icon";

export interface GenerateImageParams {
  uid?: string; // プロフィール用はUIDなしで生成可能
  options: AIGenerateOptions;
  purpose?: ImagePurpose; // デフォルトは "child_profile_icon"
  callbacks: ImageGenerationSSECallbacks;
  signal?: AbortSignal;
}

// 素材画像生成用パラメータ
export interface GenerateMaterialImageParams {
  uid: string;
  options: AIGenerateMaterialOptions;
  callbacks: ImageGenerationSSECallbacks;
  signal?: AbortSignal;
}
